import { PricingTypes, ProjectStatus, ProjectStatuses } from "../../../../common/constants"
import { Transaction } from "../../../../domain/invoicing/transaction"
import { CatalogTemplate } from "../../../../domain/catalogTemplate"
import { Workarea } from "../../../../domain/job"
import { WorkareaGroup, WorkareaGroupWithWorkareas } from "../../../../domain/workareas/group"
import { WorkareaMutationData } from "../../../../domain/workareas/workareaQueries"
import { hasValue } from "../../../../helpers/nullable"

type GroupData = {
  canEditGroupFields: boolean
  group: WorkareaGroupWithWorkareas
}

export const getQuotedPriceToCache = (selectedWorkarea?: Workarea, groups?: WorkareaGroupWithWorkareas[]): number | undefined => {
  if (!selectedWorkarea || !groups) {
    return undefined
  }

  const group = groups.find(g => g.id === selectedWorkarea.workareaGroupId)
  return (group?.pricingType === PricingTypes.QuotedPrice || group?.pricingType === PricingTypes.PerVisit)
    ? selectedWorkarea.quotedPrice
    : undefined
}

export const getGroupData = (groups: WorkareaGroupWithWorkareas[], selectedWorkarea: Workarea): GroupData => {
  const group = groups.find(g => g.id === selectedWorkarea.workareaGroupId)
  return {
    canEditGroupFields: group?.isHidden === true,
    group: group!
  }
}

export const updateWorkareaName = (workarea: Workarea, name: string): WorkareaMutationData => {
  return {
    isValidUpdate: name.length > 0 && workarea.name !== name,
    optimisticUpdate: { id: workarea.id, name},
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['Name'], name }
  }
}

export const updateWorkareaStatus = (workarea: Workarea, status: ProjectStatus): WorkareaMutationData => {
  const completedDate = status === ProjectStatuses.Completed ? new Date().toISOString() : undefined
  return {
    isValidUpdate: status !== workarea.status,
    optimisticUpdate: { id: workarea.id, status, completedDate },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['Status', 'CompletedDate'], status, completedDate }
  }
}

export const updateWorkareaCompletedDate = (workarea: Workarea, completedDate: string | null | undefined): WorkareaMutationData => {
  return {
    isValidUpdate: workarea.status === ProjectStatuses.Completed && completedDate !== workarea.completedDate,
    optimisticUpdate: { id: workarea.id, completedDate },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['CompletedDate'], completedDate }
  }
}

export const updateWorkareaDivision = (workarea: Workarea, divisionId: number): WorkareaMutationData => {
  return {
    isValidUpdate: divisionId !== workarea.divisionId,
    optimisticUpdate: { id: workarea.id, divisionId },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['DivisionId'], divisionId }
  }
}

export const updateWorkareaCostCode = (workarea: Workarea, costCodeId: number): WorkareaMutationData => {
  return {
    isValidUpdate: costCodeId !== workarea.costCodeId,
    optimisticUpdate: { id: workarea.id, costCodeId },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['CostCodeId'], costCodeId }
  }
}

export const updateWorkareaCustomLaborRateId = (workarea: Workarea, customLaborRateItemId: number | undefined | null): WorkareaMutationData => {
  return {
    isValidUpdate: customLaborRateItemId !== workarea.customLaborRateItemId,
    optimisticUpdate: { id: workarea.id, customLaborRateItemId },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['CustomLaborRateItemId'], customLaborRateItemId }
  }
}

export const updateWorkareaNotes = (workarea: Workarea, notes?: string): WorkareaMutationData => {
  return {
    isValidUpdate: notes !== workarea.notes,
    optimisticUpdate: { id: workarea.id, notes },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['Notes'], notes }
  }
}

export const updateWorkareaCustomerAndInvoiceNotes = (workarea: Workarea, customerAndInvoiceNotes?: string): WorkareaMutationData => {
  return {
    isValidUpdate: customerAndInvoiceNotes !== workarea.customerAndInvoiceNotes,
    optimisticUpdate: { id: workarea.id, customerAndInvoiceNotes },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['CustomerAndInvoiceNotes'], customerAndInvoiceNotes }
  }
}

export const updateWorkareaInvoiceDescription = (workarea: Workarea, invoiceDescription: string): WorkareaMutationData => {
  return {
    isValidUpdate: invoiceDescription !== (workarea.invoiceDescription ?? ''),
    optimisticUpdate: { id: workarea.id, invoiceDescription },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['InvoiceDescription'], invoiceDescription }
  }
}

const appendTemplateText = (original?: string | null, appending?: string): string | undefined => {
  if (!appending) {
    return original ?? undefined
  }
  if (!original) {
    return appending
  }
  return original + '<br/><br/>' + appending
}

export const applyTemplateNotes = (workarea: Workarea, template: CatalogTemplate): WorkareaMutationData => {
  const notes = appendTemplateText(workarea.notes, template.workareaInternalNotes)
  const customerAndInvoiceNotes = appendTemplateText(workarea.customerAndInvoiceNotes, template.workareaCustomerAndInvoiceNotes)
  const invoiceDescription = appendTemplateText(workarea.invoiceDescription, template.workareaInvoiceDescription)
  return {
    isValidUpdate: notes !== workarea.notes
      || customerAndInvoiceNotes !== workarea.customerAndInvoiceNotes
      || invoiceDescription !== (workarea.invoiceDescription ?? undefined),
    optimisticUpdate: { id: workarea.id, notes, customerAndInvoiceNotes, invoiceDescription },
    dataForServer: {
      jobId: workarea.jobId,
      workareaId: workarea.id,
      fieldsToUpdate: ['Notes', 'CustomerAndInvoiceNotes', 'InvoiceDescription'],
      notes,
      customerAndInvoiceNotes,
      invoiceDescription
    }
  }
}

export const updateWorkareaFinalPrice = (workarea: Workarea, customQuotedPrice: number | undefined | null, estimatedPrice: number | undefined): WorkareaMutationData => {
  const quotedPrice = customQuotedPrice ?? estimatedPrice
  return {
    isValidUpdate: customQuotedPrice !== workarea.customQuotedPrice || quotedPrice !== workarea.quotedPrice,
    optimisticUpdate: { id: workarea.id, customQuotedPrice, quotedPrice },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['CustomQuotedPrice', 'QuotedPrice'], customQuotedPrice, quotedPrice }
  }
}

export const updateWorkareaShowTaxInFinalPrice = (workarea: Workarea, showTaxInFinalPrice: boolean): WorkareaMutationData => {
  return {
    isValidUpdate: showTaxInFinalPrice !== workarea.showTaxInFinalPrice,
    optimisticUpdate: { id: workarea.id, showTaxInFinalPrice },
    dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['ShowTaxInFinalPrice'], showTaxInFinalPrice }
  }
}

export const updateWorkareaIsApproved = (workarea: Workarea, isApproved: boolean): WorkareaMutationData => {
  if(isApproved && workarea.isOnHold){
    return {
      isValidUpdate: isApproved !== workarea.isApproved,
      optimisticUpdate: { id: workarea.id, isApproved, isOnHold: false },
      dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['IsApproved', 'IsOnHold'], isApproved, isOnHold: false }
    }
  }
  else{
    return {
      isValidUpdate: isApproved !== workarea.isApproved,
      optimisticUpdate: { id: workarea.id, isApproved },
      dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['IsApproved'], isApproved }
    }
  }
}

export const updateWorkareaIsOnHold = (workarea: Workarea, isOnHold: boolean): WorkareaMutationData => {
  if(isOnHold && workarea.isApproved){
    return {
      isValidUpdate: isOnHold !== workarea.isOnHold,
      optimisticUpdate: { id: workarea.id, isOnHold, isApproved: false },
      dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['IsApproved', 'IsOnHold'], isOnHold, isApproved: false }
    }
  }
  else{
    return {
      isValidUpdate: isOnHold !== workarea.isOnHold,
      optimisticUpdate: { id: workarea.id, isOnHold },
      dataForServer: { jobId: workarea.jobId, workareaId: workarea.id, fieldsToUpdate: ['IsOnHold'], isOnHold }
    }
  }
}

export const hasChildWorkareas = (workarea: Workarea, workareaGroups: WorkareaGroupWithWorkareas[]): boolean => {
  return workareaGroups.flatMap(x => x.workareas).some(w => w.masterWorkareaId === workarea.id)
}

export const arePricingFieldsLocked = (transactions: Transaction[], workareaId: number): boolean => {
  return transactions.flatMap(x => x.elements).some(x => x.workareaId === workareaId && x.amount > 0 && x.type !== 'downPayment')
}

export const shouldUpdateQuotedPrice = (workarea: Workarea, group: WorkareaGroup, estimatedPrice: number): boolean => {
  return (group.pricingType === PricingTypes.QuotedPrice || group.pricingType === PricingTypes.PerVisit)
    && !group.isApproved
    && !hasValue(workarea.customQuotedPrice)
    && workarea.quotedPrice !== estimatedPrice
}